import { useEffect, useMemo, useState } from 'react'
import { Diagram } from '../Diagram'
import { useSelector } from 'react-redux'
import { selectedRangeSelector } from 'redux/dashboard/selectors'
import { dashboardApi } from 'api'
import { useApiRequest } from 'hooks/useApiRequest'
import { ReservationScheduleResponse } from 'api/dashboard/types'
import { diagramVariant, PERIOD } from '../../constants'
import { RESERVATION_SCHEDULE_DIAGRAM } from 'texts/dashboard'
import { BREAKDOWN_TYPES } from './constants'
import { ReservationScheduleProps } from './types'
import { useCompanyId } from 'hooks/useCompanyId'

export const ReservationsSchedule = ({
  isShortPeriod,
  carCategories,
}: ReservationScheduleProps) => {
  const [items, setItems] = useState<ReservationScheduleResponse['items']>([])
  const [period, setSelectedPeriod] = useState(PERIOD.Days)
  const selectedRange = useSelector(selectedRangeSelector)

  const companyId = useCompanyId()

  const getReservationScheduleRequest = useApiRequest(() =>
    dashboardApi.getReservationSchedule({
      companyId: String(companyId),
      dateStart: selectedRange.from,
      dateEnd: selectedRange.to,
      carCategories,
      timeGrain: period,
    })
  )

  useEffect(() => {
    if (isShortPeriod) {
      setSelectedPeriod(PERIOD.Weeks)
    }
  }, [isShortPeriod])

  useEffect(() => {
    const getSchedule = async () => {
      const response = await getReservationScheduleRequest.apiRequest()
      if (response) {
        setItems(response.data.items)
      }
    }
    getSchedule()
  }, [selectedRange.from, selectedRange.to, period, carCategories])

  const uniqueDates = [...new Set(items.map((item) => item.date))]

  const groupedData = useMemo(
    () =>
      BREAKDOWN_TYPES.map((type) => {
        const dataObj: Record<string, number> = {}
        uniqueDates.forEach((date) => {
          dataObj[date] = 0
        })

        items.forEach((item) => {
          if (item.breakdownType === type) {
            dataObj[item.date] = item.reservations
          }
        })

        const dataArray = uniqueDates.map((date) => dataObj[date] || null)

        return {
          name: type,
          data: dataArray,
        }
      }),
    [items]
  )

  return (
    <Diagram
      series={groupedData}
      categories={uniqueDates}
      onChangePeriod={setSelectedPeriod}
      columnsCount={uniqueDates.length}
      period={period}
      variant={diagramVariant.schedule}
      isShortPeriod={isShortPeriod}
      groupCount={groupedData.length}
      title={RESERVATION_SCHEDULE_DIAGRAM.TITLE}
      titleTooltip={RESERVATION_SCHEDULE_DIAGRAM.TOOLTIP}
      loading={getReservationScheduleRequest.loading}
    />
  )
}
